import { createContext, useContext, useState, useEffect } from "react";
import { useCurrentProduct } from "./useCurrentProduct";
import { useCurrentVariant } from "./useCurrentVariant";
import { useViewerMode } from "./useViewerMode";
import {getPropertyOrDefault} from "../helpers/helpers";


const configContext = createContext();

export function useConfig() {
   return useContext(configContext);
}

// Export the context for override providers (used by editor)
export { configContext };

export function ConfigProvider(props) {
   const [config, setConfig] = useState();
   const [loading, setLoading] = useState(true);
   const [languages, setLanguages] = useState();
   const [, setCurrentProduct] = useCurrentProduct();
   const [, setCurrentModel] = useCurrentVariant();
   const [, ,setViewerMode] = useViewerMode();

   const params = new URLSearchParams(window.location.search);
   const documentRoot = document.documentElement;
   const defaultProductIndex = params.get("product") ? parseInt(params.get("product"), 10) : 0;
   const defaultVariantIndex = params.get("variant") ? parseInt(params.get("variant"), 10) : 0;

   let baseUrl;
   switch(process.env.REACT_APP_ENV) {
      case "production":
         baseUrl = "https://cdn.enginecloud.co.uk";
         break;
      case "staging":
         baseUrl = "https://cdn.enginecloud.co.uk/staging";
         break;
      case "development":
         baseUrl = "https://cdn.enginecloud.co.uk/dev";
         break;
      default:
         break;
   } 
   const configLocation = params.get("config_uuid")
      ? `${baseUrl}/viewer/configs/${params.get("config_uuid")}.json`
      : "default-config.json";

   function fetchConfigData() {
      fetch(configLocation)
         .then((response) => response.json())
         .then((data) => {
            setConfig(data);
            setViewerTitle(data["viewerTitle"]);
            setRegionLanguages(getPropertyOrDefault(data["regionLanguages"], []));
            setBrandColor(data["brand-color"]);
            setHotspotColor(data["hotspot-color"]);
            setBackgroundColor(getPropertyOrDefault(data["background-color"], "#ffffff"));
            setDefaultViewerMode(data);

            // Validate product index bounds
            const validProductIndex = defaultProductIndex >= 0 && defaultProductIndex < data.products.length
               ? defaultProductIndex
               : 0;

            const product = data.products[validProductIndex];
            setCurrentProduct(product);

            // Validate variant index bounds
            const validVariantIndex = defaultVariantIndex >= 0 && defaultVariantIndex < product.variants.length
               ? defaultVariantIndex
               : 0;

            setCurrentModel(product.variants[validVariantIndex]);
         })
         .then(() => setLoading(false));
   }

   function setViewerTitle(viewerTitle) {
      
      if(viewerTitle) {
         document.title = viewerTitle
      }
      else {
         document.title = `Reydar - Model Viewer`
      }
   }

   function setDefaultViewerMode(data) {
      if(params.has("show_hotspots")) {
         setViewerMode("explore")
      } else {
         setViewerMode(getPropertyOrDefault(data["default-action"], "view"));
      }
   }
   
   function setRegionLanguages(languages) {
      setLanguages(languages);
   }

   function setBrandColor(color) {
      documentRoot.style.setProperty("--brand-color", color);
   }

   function setHotspotColor(color) {
      documentRoot.style.setProperty("--hotspot-color", color);
   }

   function setBackgroundColor(color) {
      documentRoot.style.setProperty("--background-color", color);
   }

   useEffect(() => {
      fetchConfigData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      !loading && (
         <configContext.Provider value={[config, setConfig, languages]}>
            {props.children}
         </configContext.Provider>
      )
   );
}
